import { useEffect, useRef, useCallback, useState } from 'react';


// Déduplication des notifications pour éviter les doublons
const processedNotifications = new Set();

const isNotificationProcessed = (notificationId) => {
  if (processedNotifications.has(notificationId)) {
    console.log('Notification déjà traitée, ignorée:', notificationId);
    return true;
  }
  processedNotifications.add(notificationId);
  setTimeout(() => processedNotifications.delete(notificationId), 30000);
  return false;
};

export const useNotifications = (api, role, onNotification) => {
  const [isConnected, setIsConnected] = useState(false);
  const lastCheckRef = useRef(null);
  const intervalRef = useRef(null);
  const processedIdsRef = useRef(new Set()); // Set pour eviter les doublons
  const onNotificationRef = useRef(onNotification); // Ref stable pour le callback

  // Mettre a jour la ref quand onNotification change
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = lastCheckRef.current ? `?since=${lastCheckRef.current}` : '';
      const response = await api.get(`/notifications${params}`);
      
      setIsConnected(true);
      
      const { notifications } = response.data;
      if (notifications && notifications.length > 0) {
        // Update last check time
        lastCheckRef.current = new Date().toISOString();
        
        // Process each notification - AVEC DEDUPLICATION
        const notificationIds = [];
        for (const notif of notifications) {
          // Verifier si deja traite
          if (processedIdsRef.current.has(notif.id)) {
            continue; // Ignorer les doublons
          }
          
          // Marquer comme traite
          processedIdsRef.current.add(notif.id);
          notificationIds.push(notif.id);
          
          // Appeler le callback via la ref stable
          onNotificationRef.current?.({
            type: notif.type,
            ...notif.data
          });
        }
        
        // Nettoyer le Set periodiquement (garder les 100 derniers)
        if (processedIdsRef.current.size > 100) {
          const arr = Array.from(processedIdsRef.current);
          processedIdsRef.current = new Set(arr.slice(-50));
        }
        
        // Mark as read
        if (notificationIds.length > 0) {
          try {
            await api.post('/notifications/read', notificationIds);
          } catch (e) {
            console.error('Error marking notifications as read:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setIsConnected(false);
    }
  }, [api]); // Retirer onNotification des deps - utilise la ref

  useEffect(() => {
    // Initial fetch
    lastCheckRef.current = new Date().toISOString();
    fetchNotifications();
    
    // Poll every 3 seconds for near real-time updates
    intervalRef.current = setInterval(fetchNotifications, 3000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [api]); // Deps stables - pas fetchNotifications

  return { isConnected };
};

export default useNotifications;
